import AARTextField from "@/components/AARTextField";
import BaseDrawer from "@/components/BaseDrawer";
import Btn from "@/components/Btn";
import CustomLabel from "@/components/CustomLabel";
import { ILevel } from "@/interfaces/level.interface";
import { hideLoader, showLoader } from "@/store/slices/loaderSlice";
import { RootState } from "@/store/store";
import { Button } from "@progress/kendo-react-buttons";
import { ComboBox, ComboBoxChangeEvent } from "@progress/kendo-react-dropdowns";
import { TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { set } from "lodash";
import { Image as ImageIcon, Maximize, Upload, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactCrop, { Crop, PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useDispatch, useSelector } from "react-redux";

interface IProps {
  showCreateDrawer: boolean;
  setShowCreateDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  addNewLevel: Partial<ILevel> | null;
  setAddNewLevel: React.Dispatch<
    React.SetStateAction<(ILevel & { id?: string; imageFile?: File }) | null>
  >;
  handleChange: (e: TextBoxChangeEvent | ComboBoxChangeEvent) => void;
  onSave: () => void;
}

const cropStyles = `
  .crop-preset-transition {
    transition: all 0.3s ease-in-out;
  }
  
  .image-preview-bg {
    background-color: rgba(0, 0, 0, 0.03);
  }
  
  .crop-area-bg {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 65vh;
    max-height: 75vh;
    overflow: hidden;
    background-color: rgba(0, 0, 0, 0.03);
    padding: 6px; 
  }
  
  .crop-container {
    max-height: 72vh;
    max-width: 95%;
    overflow: hidden;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .ReactCrop__drag-handle {
    width: 16px !important;
    height: 16px !important;
    background-color: !important;
    border: 2px solid white !important;
  }
  
  .ReactCrop__crop-selection {
    border: 2px solid  !important;
    border-style: solid !important;
  }
  
  .ReactCrop {
    position: relative;
    max-width: 100%;
    max-height: 65vh;
  }
  
  .ReactCrop img {
    max-height: 60vh !important;
    max-width: 100% !important;
    object-fit: contain !important;
  }
`;

type CropPreset = "free" | "1:1" | "2:3" | "16:9";

function WriteDrawer({
  showCreateDrawer,
  setShowCreateDrawer,
  addNewLevel,
  setAddNewLevel,
  handleChange,
  onSave,
  ...drawerProps
}: IProps) {
  const { projects, selectedProject } = useSelector(
    (state: RootState) => state.projects
  );

  const dispatch = useDispatch();

  const [invalidFields, setInvalidFields] = useState<{
    [key: string]: boolean;
  }>({});
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(
    addNewLevel?.imageUrl || null
  );
  const [showCropModal, setShowCropModal] = useState(false);
  const [crop, setCrop] = useState<Crop>({
    unit: "%",
    width: 100,
    height: 100,
    x: 0,
    y: 0,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<CropPreset>("free");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [originalDimensions, setOriginalDimensions] = useState<{
    width: number;
    height: number;
    displayedWidth: number;
    displayedHeight: number;
  } | null>(null);

  const [previousImagePreview, setPreviousImagePreview] = useState<
    string | null
  >(null);

  const imgRef = useRef<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = cropStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const handleValidation = (name: string, isValid: boolean) => {
    setInvalidFields((prev: any) => ({
      ...prev,
      [name]: !isValid,
    }));
  };

  const validateSave = () => {
    return (
      Object.values(invalidFields).every((field) => !field) &&
      !!(addNewLevel?.imageUrl || imageFile)
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!file.type.startsWith("image/")) {
        alert("Please upload an image file");
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setImageFile(file);
      setPreviousImagePreview(imagePreview);
      setImagePreview(previewUrl);
      setShowCropModal(true);
      setCrop({ unit: "%", width: 100, height: 100, x: 0, y: 0 });
      setCompletedCrop(null);
      setSelectedPreset("free");

      if (handleChange) {
        const customEvent = {
          target: {
            name: "imageFile",
            value: file,
          },
        } as unknown as TextBoxChangeEvent;
        handleChange(customEvent);
      }
    }

    fileInputRef.current!.value = "";
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const onImageLoad = useCallback((img: HTMLImageElement) => {
    imgRef.current = img;

    setOriginalDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight,
      displayedWidth: img.width,
      displayedHeight: img.height,
    });

    setCrop({ unit: "%", width: 100, height: 100, x: 0, y: 0 });
  }, []);

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setPreviousImagePreview(null);

    if (addNewLevel?.imageUrl) {
      setAddNewLevel((prev: any) => ({
        ...prev,
        imageUrl: "",
        imageFile: null,
      }));
    }

    if (handleChange) {
      const customEvent = {
        target: {
          name: "imageFile",
          value: null,
        },
      } as unknown as TextBoxChangeEvent;
      handleChange(customEvent);
    }
  };

  const handleOpenCropModal = () => {
    if (imagePreview) {
      setShowCropModal(true);
      setCrop({ unit: "%", width: 100, height: 100, x: 0, y: 0 });
      setCompletedCrop(null);
      setSelectedPreset("free");
    }
  };

  const handlePresetChange = (preset: CropPreset) => {
    setSelectedPreset(preset);
    setIsTransitioning(true);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);

    if (!imgRef.current) return;

    const { width, height } = imgRef.current;
    const imageAspect = width / height;

    let newCrop: Crop = { unit: "%", width: 100, height: 100, x: 0, y: 0 };

    switch (preset) {
      case "1:1":
        if (imageAspect > 1) {
          // Wide image
          newCrop = {
            unit: "%",
            width: (height / width) * 100,
            height: 100,
            x: (100 - (height / width) * 100) / 2,
            y: 0,
          };
        } else {
          // Tall image
          newCrop = {
            unit: "%",
            width: 100,
            height: (width / height) * 100,
            x: 0,
            y: (100 - (width / height) * 100) / 2,
          };
        }
        break;
      case "2:3":
        const aspect2x3 = 2 / 3;
        if (imageAspect > aspect2x3) {
          // Wide image
          newCrop = {
            unit: "%",
            width: ((height * aspect2x3) / width) * 100,
            height: 100,
            x: (100 - ((height * aspect2x3) / width) * 100) / 2,
            y: 0,
          };
        } else {
          // Tall image
          newCrop = {
            unit: "%",
            width: 100,
            height: (width / aspect2x3 / height) * 100,
            x: 0,
            y: (100 - (width / aspect2x3 / height) * 100) / 2,
          };
        }
        break;
      case "16:9":
        const aspect16x9 = 16 / 9;
        if (imageAspect > aspect16x9) {
          // Wide image
          newCrop = {
            unit: "%",
            width: ((height * aspect16x9) / width) * 100,
            height: 100,
            x: (100 - ((height * aspect16x9) / width) * 100) / 2,
            y: 0,
          };
        } else {
          // Tall image
          newCrop = {
            unit: "%",
            width: 100,
            height: (width / aspect16x9 / height) * 100,
            x: 0,
            y: (100 - (width / aspect16x9 / height) * 100) / 2,
          };
        }
        break;
      default:
        // Free crop
        newCrop = { unit: "%", width: 100, height: 100, x: 0, y: 0 };
    }

    setCrop(newCrop);

    if (imgRef.current) {
      const pixelCrop: PixelCrop = {
        unit: "px",
        x: (newCrop.x / 100) * imgRef.current.width,
        y: (newCrop.y / 100) * imgRef.current.height,
        width: (newCrop.width / 100) * imgRef.current.width,
        height: (newCrop.height / 100) * imgRef.current.height,
      };

      // Set the completedCrop state with the pixel-based crop
      setCompletedCrop(pixelCrop);
    }
  };

  const getCroppedImg = useCallback(() => {
    if (
      !completedCrop ||
      !imgRef.current ||
      !previewCanvasRef.current ||
      !originalDimensions
    )
      return;

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const crop = completedCrop;

    // Calculate the scaling factors between the displayed image and the natural image
    const scaleX = originalDimensions.width / originalDimensions.displayedWidth;
    const scaleY =
      originalDimensions.height / originalDimensions.displayedHeight;

    // Set canvas dimensions based on the crop size and scaling factors
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Draw the cropped portion of the image to the canvas
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    // Convert canvas to blob and create URL
    return new Promise<string>((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error("Canvas is empty");
            return;
          }

          const croppedImageUrl = URL.createObjectURL(blob);
          resolve(croppedImageUrl);
        },
        "image/jpeg",
        0.95
      );
    });
  }, [completedCrop, originalDimensions]);

  const handleCropComplete = (crop: PixelCrop) => {
    setCompletedCrop(crop);
  };

  const handleCancelCrop = () => {
    setShowCropModal(false);
    // Restore the previous image if available
    if (previousImagePreview) {
      setImagePreview(previousImagePreview);
      setPreviousImagePreview(null);
    }
  };

  const handleApplyCrop = async () => {
    if (!completedCrop) {
      setShowCropModal(false);
      return;
    }

    dispatch(showLoader());

    try {
      const croppedImageUrl = await getCroppedImg();

      if (croppedImageUrl) {
        setImagePreview(croppedImageUrl);
        setPreviousImagePreview(null);

        // Update the level object with cropped image
        fetch(croppedImageUrl)
          .then((res) => res.blob())
          .then((blob) => {
            const file = new File(
              [blob],
              imageFile?.name || "cropped-image.jpg",
              { type: "image/jpeg" }
            );
            setImageFile(file);

            if (handleChange) {
              const customEvent = {
                target: {
                  name: "imageFile",
                  value: file,
                },
              } as unknown as TextBoxChangeEvent;
              handleChange(customEvent);
            }
          });
      }

      setShowCropModal(false);
    } catch (e) {
      console.error("Error applying crop:", e);
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    if (!showCropModal) {
      setCrop({ unit: "%", width: 100, height: 100, x: 0, y: 0 });
      setCompletedCrop(null);
      setSelectedPreset("free");
    }
  }, [showCropModal]);

  useEffect(() => {
    if (!showCreateDrawer) {
      setImageFile(null);
      setImagePreview(null);
      setInvalidFields({});
    }
  }, [showCreateDrawer]);

  useEffect(() => {
    if (addNewLevel?.imageUrl) {
      setImagePreview(addNewLevel.imageUrl);
    }
  }, [addNewLevel?.imageUrl]);

  const renderDrawerContent = () => (
    <div className="grid gap-4">
      <div>
        <AARTextField
          name="name"
          value={addNewLevel?.name}
          onChange={handleChange}
          onValidate={(name, isValid) =>
            setInvalidFields((prev) => ({
              ...prev,
              [name]: !isValid,
            }))
          }
          label="Level Name"
          required={true}
          placeholder="Enter Name"
          disabled={false}
          regex={/[0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/}
          error="Invalid Name"
        />
      </div>

      <div>
        <div className="flex mb-2">
          <CustomLabel required label="Image" />
          {(addNewLevel?.imageUrl || imageFile) && (
            <p
              className="text-primary underline ml-auto cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <span>+</span> Upload Image
            </p>
          )}
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={handleFileChange}
          />
        </div>

        {/* Image Preview Area */}
        <div className="w-full border border-slate-200 rounded-md bg-slate-50 overflow-hidden">
          {imagePreview ? (
            <div className="relative image-preview-bg">
              <img
                src={imagePreview}
                alt="Floor plan"
                className="w-full h-[250px] object-contain"
              />
              <div className="absolute top-2 right-2 flex gap-2">
                <Button
                  fillMode="flat"
                  className="p-2 bg-white rounded-full shadow-md"
                  onClick={handleOpenCropModal}
                  title="Edit Image"
                >
                  <Maximize className="w-4 h-4 text-gray-600" />
                </Button>
                <button
                  className="p-2 bg-white rounded-full shadow-md"
                  onClick={handleRemoveImage}
                  title="Remove Image"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>
          ) : (
            <div
              className="w-full h-[250px] flex flex-col items-center justify-center cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <ImageIcon className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 mb-1">No image uploaded</p>
              <p className="text-primary font-medium text-sm">
                <Upload className="w-4 h-4 inline mr-1" />
                Click to upload
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <BaseDrawer
        isOpen={showCreateDrawer}
        onClose={() => setShowCreateDrawer(false)}
        title={addNewLevel?.id ? "Edit Level" : "Add New Level"}
        primaryActionText={"Save Level"}
        onPrimaryAction={onSave}
        primaryActionDisabled={!validateSave()}
        {...drawerProps}
        maxW="35vw"
      >
        {renderDrawerContent()}
      </BaseDrawer>

      {/* Image Crop Modal */}
      {showCropModal && imagePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 z-[99999]">
          <div className="bg-white rounded-lg shadow-lg w-[98%] h-full flex flex-col">
            <div className="px-6">
              <div className="flex items-center justify-between py-3">
                <h3 className="font-medium text-lg">Crop Image</h3>
                <button
                  className="py-2 pr-0"
                  onClick={() => setShowCropModal(false)}
                >
                  <X className="w-[26px] h-[26px] text-[#A1A1A1]" />
                </button>
              </div>
              <div className="w-full border-b"></div>
            </div>

            <div className="flex flex-col flex-1 overflow-hidden">
              <div className="crop-area-bg">
                <div className="crop-container">
                  <ReactCrop
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={handleCropComplete}
                    className={`custom-crop-container ${
                      isTransitioning ? "crop-preset-transition" : ""
                    }`}
                    aspect={
                      selectedPreset === "1:1"
                        ? 1
                        : selectedPreset === "2:3"
                        ? 2 / 3
                        : selectedPreset === "16:9"
                        ? 16 / 9
                        : undefined
                    }
                  >
                    <img
                      ref={imgRef}
                      src={imagePreview}
                      alt="Crop preview"
                      className={`${
                        isTransitioning ? "crop-preset-transition" : ""
                      }`}
                      onLoad={(e) => onImageLoad(e.currentTarget)}
                      crossOrigin="anonymous"
                    />
                  </ReactCrop>
                </div>
              </div>
            </div>

            <div className="w-full border-b"></div>

            <div className="flex justify-between gap-x-6 py-4 px-6 ">
              <div className="flex gap-3">
                <Button
                  togglable={true}
                  selected={selectedPreset === "free"}
                  onClick={() => handlePresetChange("free")}
                  className={`text-[10px] w-[68px] h-[74px] border-[#8A8886] ${
                    selectedPreset === "free"
                      ? "bg-primary text-white"
                      : "bg-white text-label"
                  }`}
                >
                  <div className="flex flex-col justify-center items-center gap-y-2">
                    <div
                      className={`font-normal border ${
                        selectedPreset === "free"
                          ? "border-white"
                          : "border-[#8A8886]"
                      } border-dashed w-[20px] h-[20px]`}
                    ></div>
                    <p className="font-normal">Free Crop</p>
                  </div>
                </Button>
                <Button
                  togglable={true}
                  selected={selectedPreset === "1:1"}
                  onClick={() => handlePresetChange("1:1")}
                  className={`text-[10px] w-[68px] h-[74px] border-[#8A8886] ${
                    selectedPreset === "1:1"
                      ? "bg-primary text-white"
                      : "bg-white text-label"
                  }`}
                >
                  <div className="flex flex-col justify-center items-center gap-y-2">
                    <div
                      className={`font-normal border ${
                        selectedPreset === "1:1"
                          ? "border-white"
                          : "border-[#8A8886]"
                      } w-[25px] h-[25px]`}
                    ></div>
                    <p className="font-normal">1:1</p>
                  </div>
                </Button>
                <Button
                  togglable={true}
                  selected={selectedPreset === "2:3"}
                  onClick={() => handlePresetChange("2:3")}
                  className={`text-[10px] w-[68px] h-[74px] border-[#8A8886] ${
                    selectedPreset === "2:3"
                      ? "bg-primary text-white"
                      : "bg-white text-label"
                  }`}
                >
                  <div className="flex flex-col justify-center items-center gap-y-2">
                    <div
                      className={`font-normal border ${
                        selectedPreset === "2:3"
                          ? "border-white"
                          : "border-[#8A8886]"
                      } w-[16px] h-[25px]`}
                    ></div>
                    <p className="font-normal">2:3</p>
                  </div>
                </Button>
                <Button
                  togglable={true}
                  selected={selectedPreset === "16:9"}
                  onClick={() => handlePresetChange("16:9")}
                  className={`text-[10px] w-[68px] h-[74px] border-[#8A8886] ${
                    selectedPreset === "16:9"
                      ? "bg-primary text-white"
                      : "bg-white text-label"
                  }`}
                >
                  <div className="flex flex-col justify-center items-center gap-y-2">
                    <div
                      className={`border ${
                        selectedPreset === "16:9"
                          ? "border-white"
                          : "border-[#8A8886]"
                      } w-[25px] h-[16px]`}
                    ></div>
                    <p className="font-normal relative top-[3px]">16:9</p>
                  </div>
                </Button>
              </div>
              <div className="flex gap-x-6 self-center">
                <div className="w-[181px]">
                  <Btn
                    variant="tertiary"
                    text={"Cancel"}
                    border
                    disabled={false}
                    onClick={handleCancelCrop}
                  />
                </div>
                <div className="w-[181px]">
                  <Btn
                    variant="primary"
                    text={"Crop"}
                    onClick={handleApplyCrop}
                  />
                </div>
              </div>
            </div>

            {/* Hidden canvas for cropping */}
            <canvas ref={previewCanvasRef} style={{ display: "none" }} />
          </div>
        </div>
      )}
    </>
  );
}

export default WriteDrawer;
